import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { AccessStatus } from "@/components/AccessStatus";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBytes } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UploadCloud, FileAudio, Loader2, ShieldCheck, X, RotateCw } from "lucide-react";
import { toast } from "sonner";

export default function UploadPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [speakerInfo, setSpeakerInfo] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [access, setAccess] = useState(user?.access);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setAccess(data.access)).catch(() => {});
  }, []);

  const canAnalyze = access?.can_analyze;

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("audio/")) {
      toast.error("Please select an audio file.");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    pick(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file) return toast.error("Choose an audio file first.");
    if (!consent) return toast.error("Please confirm you are legally permitted to submit this audio.");
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("consent", "true");
      form.append("speaker_info", speakerInfo);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Analysis complete");
      navigate(`/reports/${data.report_id}`);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.";
      toast.error(msg);
      // 402 = no access -> billing. 422/413/400 = analysis/validation error; credit NOT used, stay here.
      if (err.response?.status === 402) navigate("/billing");
      // refresh access so the (unchanged) credit count stays accurate after a failed analysis
      api.get("/stats").then(({ data }) => setAccess(data.access)).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      title="New Analysis"
      subtitle="Upload an audio conversation you are legally permitted to submit."
    >
      <div className="space-y-6">
        <div
          data-testid="rotate-hint"
          className="hidden max-[430px]:portrait:flex items-center gap-2 rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-3 py-2 text-xs text-cyan-300"
        >
          <RotateCw className="h-3.5 w-3.5 shrink-0" />
          For a wider view, rotate your phone
        </div>
        <AccessStatus access={access} suppressSelfCta />


        <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-6 sm:p-8">
          {!canAnalyze && (
            <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300" data-testid="no-access-warning">
              You don't have an active analysis credit. Uploading requires a plan — you'll be
              directed to purchase after submitting.
            </div>
          )}

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="upload-dropzone"
            className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 px-6 py-14 text-center transition-colors hover:border-cyan-400/50 hover:bg-slate-900/60"
          >
            <input
              ref={inputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              data-testid="file-input"
              onChange={(e) => pick(e.target.files?.[0])}
            />
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-400/10">
              <UploadCloud className="h-6 w-6 text-cyan-400" />
            </div>
            <p className="mt-4 font-medium text-white">Drop your audio file here, or click to browse</p>
            <p className="mt-1 text-sm text-slate-500">Common audio formats supported</p>
          </div>

          {file && (
            <div className="mt-5 flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-900/60 px-4 py-3" data-testid="selected-file">
              <FileAudio className="h-5 w-5 text-cyan-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-white">{file.name}</p>
                <p className="text-xs text-slate-500 font-mono">{formatBytes(file.size)}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                data-testid="remove-file"
                className="text-slate-500 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="mt-6">
            <label htmlFor="speaker-info" className="block text-sm font-medium text-slate-300">
              Speaker information <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="speaker-info"
              type="text"
              value={speakerInfo}
              onChange={(e) => setSpeakerInfo(e.target.value)}
              data-testid="speaker-info-input"
              placeholder="e.g. Speaker A = Alex (manager), Speaker B = Jordan (client)"
              className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-cyan-400/60 focus:outline-none"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              Helps the analysis attribute statements accurately. Sent along with the transcript.
            </p>
          </div>

          <label className="mt-6 flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 cursor-pointer">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              data-testid="consent-checkbox"
              className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-black"
            />
            <span className="text-sm text-slate-300">
              I confirm I am <span className="text-white font-medium">legally permitted</span> to
              submit this audio for analysis and that I have any consent required by applicable law.
            </span>
          </label>

          <Button
            onClick={submit}
            disabled={loading || !file || !consent}
            data-testid="analyze-btn"
            className="mt-6 w-full h-12 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow disabled:opacity-40"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Transcribing &amp; analyzing…</>
            ) : (
              <>Run Communication Analysis</>
            )}
          </Button>

          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-cyan-400" />
            Glexis provides communication-clarity insights only. It is not a lie detector and makes
            no claims about honesty, intent, or legal matters.
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
